import { useId, useState, type MouseEvent, type ReactNode } from 'react';
import { ArrowDown2 } from 'iconsax-reactjs';
import { Tooltip, cx } from '@/components/ui';
import { useAppShell } from '../AppShell/appShellContext';
import { containsItem, findActiveItemId, type NavItem, type NavSection } from './navUtils';
import styles from './SideNav.module.css';

export interface NavLinkProps {
  className: string;
  'aria-current'?: 'page';
  onClick: (event: MouseEvent<HTMLElement>) => void;
}

export interface SideNavProps {
  sections: NavSection[];
  /** Current pathname; drives the active state. */
  activeHref?: string;
  /**
   * Renders a navigation link (inject your router's Link). Spread `props` on it and render
   * `children` inside. Defaults to a plain <a href>.
   */
  renderLink?: (
    item: NavItem & { href: string },
    props: NavLinkProps,
    children: ReactNode,
  ) => ReactNode;
  /** Icon-rail mode. Defaults to the AppShell state. */
  collapsed?: boolean;
  /** Brand area (e.g. <SideNavBrand />). */
  header?: ReactNode;
  footer?: ReactNode;
  /** Accessible name of the navigation landmark. */
  label?: string;
  /** Called after a link is activated (the AppShell drawer closes automatically). */
  onNavigate?: (item: NavItem) => void;
  className?: string;
}

/** Primary navigation. Presentational: it renders only the sections it is given. */
export function SideNav({
  sections,
  activeHref,
  renderLink,
  collapsed: collapsedProp,
  header,
  footer,
  label = 'Primary',
  onNavigate,
  className,
}: SideNavProps) {
  const shell = useAppShell();
  const collapsed = collapsedProp ?? shell?.collapsed ?? false;
  const baseId = useId();
  const activeId = findActiveItemId(sections, activeHref);
  // Explicit user choices; groups without a choice are open when they contain the active item.
  const [expandedOverrides, setExpandedOverrides] = useState<Record<string, boolean>>({});

  function isExpanded(item: NavItem): boolean {
    return expandedOverrides[item.id] ?? containsItem(item, activeId);
  }

  function toggleGroup(item: NavItem) {
    if (collapsed) {
      // In the icon rail, opening a group expands the rail so its links are visible.
      shell?.setCollapsed(false);
      setExpandedOverrides((prev) => ({ ...prev, [item.id]: true }));
      return;
    }
    setExpandedOverrides((prev) => ({ ...prev, [item.id]: !isExpanded(item) }));
  }

  function handleNavigate(item: NavItem) {
    shell?.closeMobileNav();
    onNavigate?.(item);
  }

  function renderItemContent(item: NavItem, depth: number, isActive: boolean) {
    const Icon = item.icon;
    return (
      <>
        {Icon ? (
          <span className={styles.icon} aria-hidden="true">
            <Icon size={20} color="currentColor" variant={isActive ? 'Bold' : 'Linear'} />
          </span>
        ) : (
          depth === 0 && <span className={styles.iconPlaceholder} aria-hidden="true" />
        )}
        <span className={cx(styles.label, collapsed && 'sr-only')}>{item.label}</span>
        {item.badge != null && (
          <span className={cx(styles.badge, collapsed && styles.badgeRail)}>
            <span className={cx(collapsed && 'sr-only')}>{item.badge}</span>
          </span>
        )}
      </>
    );
  }

  function renderItem(item: NavItem, depth: number): ReactNode {
    const hasChildren = Boolean(item.children && item.children.length > 0);

    if (hasChildren) {
      const expanded = !collapsed && isExpanded(item);
      const groupId = `${baseId}-${item.id}`;
      const containsActive = containsItem(item, activeId);
      const button = (
        <button
          type="button"
          className={cx(
            styles.link,
            styles.groupButton,
            containsActive && styles.groupActive,
            depth > 0 && styles.nested,
            depth > 1 && styles.nestedDeep,
          )}
          aria-expanded={expanded}
          aria-controls={expanded ? groupId : undefined}
          onClick={() => toggleGroup(item)}
        >
          {renderItemContent(item, depth, containsActive)}
          {!collapsed && (
            <ArrowDown2
              size={14}
              color="currentColor"
              className={cx(styles.chevron, expanded && styles.chevronOpen)}
              aria-hidden="true"
            />
          )}
        </button>
      );
      return (
        <li key={item.id} className={styles.item}>
          {collapsed ? (
            <Tooltip content={item.label} placement="right" describeChild={false} delay={150}>
              {button}
            </Tooltip>
          ) : (
            button
          )}
          {expanded && (
            <ul id={groupId} className={styles.subList}>
              {item.children?.map((child) => renderItem(child, depth + 1))}
            </ul>
          )}
        </li>
      );
    }

    if (!item.href) return null;
    const isActive = item.id === activeId;
    const props: NavLinkProps = {
      className: cx(
        styles.link,
        isActive && styles.active,
        depth > 0 && styles.nested,
        depth > 1 && styles.nestedDeep,
      ),
      ...(isActive ? { 'aria-current': 'page' as const } : {}),
      onClick: () => handleNavigate(item),
    };
    const children = renderItemContent(item, depth, isActive);
    const link = renderLink ? (
      renderLink({ ...item, href: item.href }, props, children)
    ) : (
      <a
        href={item.href}
        className={props.className}
        aria-current={props['aria-current']}
        onClick={props.onClick}
      >
        {children}
      </a>
    );

    return (
      <li key={item.id} className={styles.item}>
        {collapsed ? (
          <Tooltip content={item.label} placement="right" describeChild={false} delay={150}>
            {link}
          </Tooltip>
        ) : (
          link
        )}
      </li>
    );
  }

  return (
    <nav aria-label={label} className={cx(styles.nav, collapsed && styles.collapsed, className)}>
      {header && <div className={styles.header}>{header}</div>}
      <div className={styles.scroll}>
        {sections.map((section) => {
          const headingId = `${baseId}-section-${section.id}`;
          return (
            <div key={section.id} className={styles.section}>
              {section.label && (
                <h2 id={headingId} className={cx(styles.sectionLabel, collapsed && 'sr-only')}>
                  {section.label}
                </h2>
              )}
              <ul className={styles.list} aria-labelledby={section.label ? headingId : undefined}>
                {section.items.map((item) => renderItem(item, 0))}
              </ul>
            </div>
          );
        })}
      </div>
      {footer && <div className={styles.footer}>{footer}</div>}
    </nav>
  );
}
